import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import DOMPurify from 'dompurify';
import { db, collection, addDoc, doc, deleteDoc, updateDoc, query, where, getDocs } from '../firebaseConfig';
import Card from "@mui/material/Card";
import MenuBookIcon from '@mui/icons-material/MenuBook';
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import IconButton from '@mui/material/IconButton';
import DialogTitle from "@mui/material/DialogTitle";
import InfoIcon from '@mui/icons-material/Info';
import MDBox from "components/MDBox";
import Tooltip from '@mui/material/Tooltip';
import MDTypography from "components/MDTypography";
import Divider from "@mui/material/Divider";
import InputLabel from "@mui/material/InputLabel";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import { Editor } from 'primereact/editor';
import Accordion from '@mui/material/Accordion';
import { useTheme } from '@mui/material/styles';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './TradeDiary.css'; // Import the CSS file

const TradeDiary = ({ userID }) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [diaryEntry, setDiaryEntry] = useState('');
  const [DiaryID, setDiaryID] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [previousEntries, setPreviousEntries] = useState([]);
  const [isLoadingEntry, setIsLoadingEntry] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const theme = useTheme();
  const iconColor = theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[600];
  const textColor = theme.palette.mode === 'dark' ? '#ffffff' : '#000000';

  useEffect(() => {
    const fetchDiary = async () => {
      setIsLoadingEntry(true);
      const formattedDate = selectedDate.toISOString().split('T')[0];
      try {
        const q = query(collection(db, 'Users', userID, 'diary'), where('date', '==', formattedDate));
        const diarySnapshot = await getDocs(q);
        if (!diarySnapshot.empty) {
          const data = diarySnapshot.docs[0].data();
          setDiaryEntry(data.entry || ''); // Ensure we handle null/undefined
          setDiaryID(diarySnapshot.docs[0].id);
        } else {
          setDiaryEntry('');
          setDiaryID('');
        }
      } catch (error) {
        console.error('Error fetching diary entry:', error);
      } finally {
        setIsLoadingEntry(false);
      }
    };

    const fetchPreviousEntries = async () => {
      try {
        const q = query(collection(db, 'Users', userID, 'diary'));
        const diarySnapshot = await getDocs(q);
        const entries = diarySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        entries.sort((a, b) => new Date(b.date) - new Date(a.date));  // Sort by date in descending order
        setPreviousEntries(entries);
      } catch (error) {
        console.error('Error fetching previous entries:', error);
      }
    };

    fetchDiary();
    fetchPreviousEntries();
  }, [userID, selectedDate]);

  const saveDiaryEntry = async () => {
    try {
      if (DiaryID) {
        const diaryRef = doc(db, 'Users', userID, 'diary', DiaryID);
        await updateDoc(diaryRef, { entry: diaryEntry });
      } else {
        await addDoc(collection(db, 'Users', userID, 'diary'), {
          userID: userID,
          date: selectedDate.toISOString().split('T')[0],
          entry: diaryEntry,
        });
      }
    setSuccessMessage('Diary Entry Saved Successfully');
    } catch (error) {
      console.error('Error adding/editing diary entry: ', error);
    }
  };


  
  const deleteDiaryEntry = async () => {
    setShowConfirmation(true);
  };

  const confirmDelete = async () => {
    const diaryRef = doc(db, 'Users', userID, 'diary', DiaryID);
    await deleteDoc(diaryRef);
    setShowConfirmation(false);
    setDiaryEntry('');
    setDiaryID('');
    setSuccessMessage('Diary entry deleted successfully!');
  };

  const cancelDelete = () => {
    setShowConfirmation(false);
  };

  useEffect(() => {
    if (successMessage) {
      setTimeout(() => {
        setSuccessMessage('');
      }, 2000);
    }
  }, [successMessage]);

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const renderEntriesByYearAndMonth = () => {
    const entriesByYear = previousEntries.reduce((acc, entry) => {
      const [year, month, day] = entry.date.split('-');
      acc[year] = acc[year] || {};
      acc[year][month] = acc[year][month] || [];
      acc[year][month].push(entry);
      return acc;
    }, {});

    return Object.keys(entriesByYear).map(year => (
      <Accordion key={year} style={{ backgroundColor: 'inherit', color: 'inherit' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <MDTypography>{year}</MDTypography>
        </AccordionSummary>
        <AccordionDetails sx={{ color: iconColor }}>
          {Object.keys(entriesByYear[year]).map(month => (
            <Accordion key={month} style={{ backgroundColor: 'inherit', color: 'inherit' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <MDTypography>{monthNames[month - 1]}</MDTypography>
              </AccordionSummary>
              <AccordionDetails>
                {entriesByYear[year][month].map(entry => (
                  <Accordion key={entry.id} style={{ backgroundColor: 'inherit', color: 'inherit' }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <MDTypography>{entry.date.split('-')[2]}</MDTypography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(entry.entry) }} />
                    </AccordionDetails>
                  </Accordion>
                ))}
              </AccordionDetails>
            </Accordion>
          ))}
        </AccordionDetails>
      </Accordion>
    ));
  };

  const tooltipContent = `The Trade Diary Features include daily journal entries with a rich text editor for detailed notes, the ability to view entries by year/month and options to edit or delete previous entries—helping you track your trading thoughts, strategies, and lessons learned.`;

  return (
    <Card sx={{ 
      minHeight: '800px',
      backgroundColor: 'inherit',
      borderRadius: '12px',
      overflow: 'hidden'
    }}>
      <MDBox display="flex" flexDirection="column" height="100%" p={0}>
        {/* Header */}
        <MDBox 
          display="flex" 
          justifyContent="space-between" 
          alignItems="center"
          p={3}
          borderBottom={`1px solid ${theme.palette.divider}`}
        >
          <MDBox display="flex" alignItems="center">
            <MDBox
              variant="gradient"
              bgColor="light"
              color="dark"
              borderRadius="xl"
              display="flex"
              justifyContent="center"
              alignItems="center"
              width="3rem"
              height="3rem"
              mr={2}
            >
              <MenuBookIcon fontSize="medium" color="inherit" />
            </MDBox>
            <MDTypography variant="h6" fontWeight="medium">
              Trade Diary
            </MDTypography>
          </MDBox>
          <Tooltip 
  title={tooltipContent}
  placement="bottom-end"
  componentsProps={{
    tooltip: {
      sx: {
        bgcolor: theme.palette.background.paper,
        color: iconColor,
        border: `1px solid ${theme.palette.divider}`,
        p: 1.5,
        maxWidth: '250px',
        fontSize: '0.75rem',
        lineHeight: 1.5,
        '& .MuiTooltip-arrow': {
          color: iconColor,
        }
      }
    }
  }}
>
  <InfoIcon 
    sx={{ 
      ml: 1, 
      fontSize: '1rem',
      color: iconColor,
      cursor: 'help'
    }} 
  />
</Tooltip>
        </MDBox>
  
        {/* Main Content */}
        <MDBox display="flex" flexGrow={1} p={0} height="calc(100vh - 200px)">
          {/* Editor Section */}
          <MDBox 
            width="65%" 
            display="flex" 
            flexDirection="column" 
            p={3} 
            sx={{ borderRight: `1px solid ${theme.palette.divider}` }}
          >
            <MDBox display="flex" alignItems="center" mb={3}>
              <MDInput
                type="date"
                value={selectedDate.toISOString().split('T')[0]}
                onChange={(e) => setSelectedDate(new Date(e.target.value))}
                sx={{
                  width: '200px',
                  '& .MuiInputBase-input': {
                    padding: '10px',
                    borderRadius: '8px'
                  }
                }}
              />
              <MDBox ml="auto" display="flex" gap={1}>
                <MDButton 
                  onClick={saveDiaryEntry} 
                  color="success" 
                  variant="contained"
                  size="small"
                >
                  Save Entry
                </MDButton>
                {DiaryID && (
                  <MDButton 
                    onClick={deleteDiaryEntry} 
                    color="error" 
                    variant="outlined"
                    size="small"
                  >
                    Delete
                  </MDButton>
                )}
              </MDBox>
            </MDBox>
  
            {successMessage && (
              <MDBox 
                bgcolor="success.main" 
                color="white" 
                p={2} 
                mb={2} 
                borderRadius="lg"
                sx={{ 
                  animation: 'fadeIn 0.3s ease-in',
                  position: 'absolute',
                  top: '20px',
                  right: '20px',
                  zIndex: 1000
                }}
              >
                {successMessage}
              </MDBox>
            )}
  
  <ReactQuill
  theme="snow"
  value={diaryEntry}
  onChange={setDiaryEntry}
  readOnly={isLoadingEntry}
  modules={{
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ 'header': 1 }, { 'header': 2 }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'script': 'sub'}, { 'script': 'super' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      ['link', 'image'],
      ['clean']
    ],
  }}
  style={{
    height: 'calc(100% - 40px)',
    minHeight: '400px',
    borderRadius: '8px',
    backgroundColor: 'inherit',
    color: iconColor,
  }}
/>
          </MDBox>
  
          {/* Previous Entries Sidebar */}
          <MDBox 
            width="35%" 
            p={3} 
            sx={{ 
              overflowY: 'auto',
              backgroundColor: theme.palette.background.light,
            }}
          >
            <MDTypography 
              variant="h6" 
              fontWeight="medium" 
              mb={3}
            >
              Previous Entries
            </MDTypography>
            <MDBox>{renderEntriesByYearAndMonth()}</MDBox>
          </MDBox>
        </MDBox>
      </MDBox>
  
      {/* Delete Confirmation Dialog */}
      <Dialog 
  open={showConfirmation} 
  onClose={cancelDelete}
  PaperProps={{
    sx: {
      borderRadius: '14px',
      bgcolor: theme.palette.background.default,
      padding: '20px',
      width: '400px',
      color: iconColor,
      '& .MuiDialogContent-root': {
        color: iconColor,
      },
      '& .MuiDialogContentText-root': {
        color: iconColor,
      },
      '& .MuiDialogTitle-root': {
        color: iconColor,
      }
    }
  }}
  BackdropProps={{
    sx: {
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
    }
  }}
>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this entry? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
        <MDButton onClick={cancelDelete} color="warning" sx={{ marginRight: 'auto' }}>
            Cancel
          </MDButton>
          <MDButton onClick={confirmDelete} color="error" variant="contained">
            Delete
          </MDButton>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

TradeDiary.propTypes = {
  userID: PropTypes.string.isRequired,
};

export default TradeDiary;
